import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Plus, Zap } from 'lucide-react';

import { kinds } from '@/app/site/scope';
import { useSiteScope } from '@/app/site/useSiteScope';
import { CatalogHead, CatalogTable } from '@/features/catalog/Catalog';
import catalog from '@/features/catalog/Catalog.module.scss';
import { automationsList } from '@/shared/api/generated/automations/automations';
import { describeWhen } from '@/shared/lib/dates';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';

import { ACTION_TITLES, triggerText } from './automations';

const RUN: Record<string, { label: string; tone: 'success' | 'danger' | 'warning' }> = {
  ok: { label: 'прошла', tone: 'success' },
  failed: { label: 'ошибка', tone: 'danger' },
  partial: { label: 'частично', tone: 'warning' },
};

/**
 * «Автоматизации» (шаг 7.4): что происходит само, когда на сайте что-то
 * случилось. Свои у каждого окружения: на Development их проверяют, не
 * тревожа команду.
 */
export function AutomationsPage() {
  const { siteId, env } = useParams({ from: '/authed/sites/$siteId/$env/automations/' });
  const navigate = useNavigate();
  const scope = useSiteScope();

  const list = useQuery({
    queryKey: ['automations', siteId, env],
    queryFn: async () => {
      const response = await automationsList(siteId, kinds[env]);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на список автоматизаций');
      }

      return response.data.items ?? [];
    },
  });

  const create = () => {
    void navigate({ to: '/sites/$siteId/$env/automations/new', params: { siteId, env } });
  };

  const items = list.data ?? [];

  return (
    <div className={catalog['page']}>
      <CatalogHead
        title="Автоматизации"
        subtitle="Что происходит само: пришла заявка — письмо команде и сообщение в Telegram, заказ оплачен — вебхук в склад, отзыв вернули на доработку — письмо автору."
        actions={
          <Button iconLeft={<Plus size={15} />} onClick={create}>
            Новая автоматизация
          </Button>
        }
      />

      {list.isPending ? null : items.length === 0 ? (
        <EmptyState
          icon={<Zap />}
          title="Автоматизаций пока нет"
          description={`Начните с письма о новой заявке: «когда» — новая запись в форме с сайта, «что» — письмо на вашу почту. На ${
            env === 'dev' ? 'Development' : 'Production'
          } — свои автоматизации.`}
          action={<Button onClick={create}>Завести первую</Button>}
        />
      ) : (
        <CatalogTable head={['Название', 'Когда', 'Что', 'Последний запуск', '']}>
          {items.map((automation) => {
            const run =
              automation.last_run === undefined ? undefined : RUN[automation.last_run.status];

            return (
              <tr key={automation.id}>
                <td>
                  <Link
                    to="/sites/$siteId/$env/automations/$automationId"
                    params={{ siteId, env, automationId: automation.id }}
                  >
                    {automation.name}
                  </Link>
                </td>
                <td>{triggerText(automation, scope?.types ?? [])}</td>
                <td>{(automation.actions ?? []).map((a) => ACTION_TITLES[a.kind]).join(', ')}</td>
                <td>
                  {automation.last_run === undefined || run === undefined ? (
                    '—'
                  ) : (
                    <>
                      <Badge size="sm" tone={run.tone}>
                        {run.label}
                      </Badge>{' '}
                      {describeWhen(automation.last_run.created_at)}
                    </>
                  )}
                </td>
                <td>
                  {!automation.enabled && (
                    <Badge size="sm" tone="neutral">
                      выключена
                    </Badge>
                  )}
                </td>
              </tr>
            );
          })}
        </CatalogTable>
      )}
    </div>
  );
}
